import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Divider,
  Avatar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import api from "../../../../utils/apiConfig";
import InsuranceSection from "./health-life/InsuranceSection";
import EstatePlanSection from "./health-life/EstatePlanSection";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import UnifiedAssessmentCard from "./health-life/UnifiedAssessmentCard";
import UnifiedAssessmentWizard from "./health-life/UnifiedAssessmentWizard";
import { useTheme } from "../../../../contexts/ThemeContext";

import logger from '../../../../utils/logger';

const EXERCISE_FREQUENCY = [
  { value: 0, label: "Never" },
  { value: 1, label: "1-2 times/week" },
  { value: 2, label: "3-4 times/week" },
  { value: 3, label: "5+ times/week" },
];

const STRESS_LEVELS = [
  { value: 1, label: "Low" },
  { value: 2, label: "Moderate" },
  { value: 3, label: "High" },
  { value: 4, label: "Severe" },
];

// Helper function to map database status to display status
const mapStatusToDisplay = (dbStatus) => {
  switch (dbStatus) {
    case "Executed":
      return "Active";
    case "Needs_Review":
      return "Needs Update";
    case "Draft":
      return "In Progress";
    default:
      return dbStatus;
  }
};

const formatName = (nameObj) => {
  if (!nameObj) return "";
  const {
    prefix = "",
    first_name = "",
    preferred_name = "",
    middle_name = "",
    last_name = "",
    suffix = "",
  } = nameObj;

  const nameParts = [
    prefix,
    preferred_name || first_name,
    middle_name,
    last_name,
    suffix,
  ].filter((part) => part); // Remove empty strings

  return nameParts.join(" ").trim();
};

const HealthLife = () => {
  const { householdId, userId } = useParams();
  const { isDarkMode } = useTheme();
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState({ memberId: null, type: null });
  const [expanded, setExpanded] = useState(["health", "insurance", "estate"]);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        if (!householdId || !userId) {
          throw new Error("Missing required parameters");
        }

        setError(null);
        setIsLoading(true);

        logger.info(
          `📋 Fetching health & life data for household: ${householdId}, user: ${userId}`,
        );
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/health-life`,
        );

        logger.info("✅ Health & life response:", response.data);

        if (!response.data || !Array.isArray(response.data.members)) {
          logger.error("Invalid response structure:", response.data);
          throw new Error("Invalid response format: missing members data");
        }

        // Fetch estate planning data for each member
        const membersWithData = await Promise.all(
          response.data.members.map(async (member) => {
            try {
              // Fetch estate planning data
              const estatePlanResponse = await api.get(
                `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/health-life/${member.id}/estate-plan`,
              );

              logger.info(
                `Estate plan response for user ${member.id}:`,
                estatePlanResponse.data,
              );

              // Transform estate plan data to frontend format
              const transformedResponse = {
                hasEstatePlan: estatePlanResponse.data.has_estate_plan,
                estatePlanDetails: estatePlanResponse.data.plan_details || [],
                futureInterest: {
                  status: estatePlanResponse.data.interest_status
                    ? "interested"
                    : "not_interested",
                  preferredTimeframe:
                    estatePlanResponse.data.preferred_timeline,
                  notes: estatePlanResponse.data.additional_notes_concerns,
                },
              };

              return {
                ...member,
                estatePlan: transformedResponse,
              };
            } catch (error) {
              logger.error(
                `Error fetching estate plan data for user ${member.id}:`,
                error,
              );
              return member;
            }
          }),
        );

        // Fetch family members for each member in the household
        const membersWithFamilyData = await Promise.all(
          membersWithData.map(async (member) => {
            try {
              // Fetch family members for this user
              logger.info(`Fetching profile data for user ${member.id}`);
              const profileResponse = await api.get(
                `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${member.id}`,
              );

              logger.info(
                `Profile response for user ${member.id}:`,
                profileResponse.data,
              );

              // Extract family members from the profile response
              const familyMembers = profileResponse.data?.family_members || [];

              // Log siblings specifically to help with debugging
              const siblings = familyMembers.filter(
                (fm) => fm.relationship_id === 23 || fm.relationship_id === 24,
              );
              logger.info(
                `Found ${siblings.length} siblings for user ${member.id}:`,
                siblings,
              );

              // Also try direct DB query for siblings if needed
              let enhancedFamilyMembers = [...familyMembers];

              // If we have siblings in the DB but not in family_members, add them to the recommended assessment
              const recommendedAssessment = member.recommendedAssessment || {};
              if (siblings.length > 0 && recommendedAssessment) {
                // Convert siblings to the format expected by RecommendedAssessmentStep
                const formattedSiblings = siblings.map((s) => ({
                  relationship: s.relationship_id === 23 ? "brother" : "sister",
                  dateOfBirth: s.date_of_birth,
                  dateOfDeath: s.date_of_death,
                  isDeceased: !s.is_living,
                  notes: s.health_notes || "",
                }));

                // Add siblings directly to the recommendedAssessment
                recommendedAssessment.siblings = formattedSiblings;

                logger.info(
                  "Enhanced recommendedAssessment with siblings:",
                  recommendedAssessment,
                );
              }

              return {
                ...member,
                familyMembers: enhancedFamilyMembers,
                recommendedAssessment,
              };
            } catch (error) {
              logger.error(
                `Error fetching profile data for user ${member.id}:`,
                error,
              );
              return {
                ...member,
                familyMembers: [],
              };
            }
          }),
        );

        // Rest of your existing member data processing...
        const finalMembersData = membersWithFamilyData.map((member) => ({
          id: member.id,
          name:
            member.name ||
            `${member.firstName || ""} ${member.lastName || ""}`.trim(),
          firstName: member.firstName || "",
          lastName: member.lastName || "",
          dateOfBirth: member.dateOfBirth,
          gender: member.gender,
          contact: member.contact || null,
          familyMembers: member.familyMembers || [],
          basicAssessment: member.basicAssessment
            ? {
                ...member.basicAssessment,
                isCompleted: !!member.basicAssessment.assessment_date,
              }
            : null,
          recommendedAssessment: member.recommendedAssessment || null,
          advancedAssessment: member.advancedAssessment || null,
          insuranceInfo: member.insuranceInfo || {
            lifeInsurance: [],
            additionalCoverage: [],
          },
          estatePlan: member.estatePlan || {
            hasEstatePlan: false,
            futureInterest: {
              status: "",
              preferredTimeline: "",
              notes: "",
            },
          },
        }));

        logger.info("Final members data:", finalMembersData);
        setHouseholdMembers(finalMembersData);
      } catch (error) {
        logger.error("Error fetching client data:", error);
        setError("Failed to fetch client data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [householdId, userId]);

  // Modal handlers
  const handleModalToggle = (memberId, type, open) => {
    setModalOpen(open ? { memberId, type } : { memberId: null, type: null });
  };

  // Data update handlers
  const handleUpdate = async (memberId, section, data) => {
    try {
      logger.info("Updating section:", section, "with data:", data);

      // Special handling for estate planning data
      if (section === "estatePlan") {
        const estatePlanData = {
          hasEstatePlan: data.hasEstatePlan,
          interestStatus: data.futureInterest?.status === "interested",
          preferredTimeline: data.futureInterest?.preferredTimeframe || null,
          additionalNotesConcerns: data.futureInterest?.notes || "",
          status: data.hasEstatePlan
            ? "Active"
            : data.futureInterest?.status === "interested"
              ? "Interested"
              : "Not Started",
          planDetails: data.estatePlanDetails || [],
        };

        const response = await api.post(
          `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/health-life/${memberId}/estate-plan`,
          estatePlanData,
        );

        // Transform the response back to frontend format
        const transformedResponse = {
          hasEstatePlan: response.data.data.has_estate_plan,
          estatePlanDetails: response.data.data.plan_details || [],
          futureInterest: {
            status: response.data.data.interest_status
              ? "interested"
              : "not_interested",
            status: response.data.interest_status
              ? "interested"
              : "not_interested",
            preferredTimeline: response.data.preferred_timeline,
            notes: response.data.additional_notes_concerns,
          },
        };

        // Update local state
        setHouseholdMembers((prevMembers) =>
          prevMembers.map((member) => {
            if (member.id === memberId) {
              return {
                ...member,
                estatePlan: transformedResponse,
              };
            }
            return member;
          }),
        );

        return;
      }

      // Handle other sections
      const response = await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${memberId}/health-life/${section}`,
        data,
      );

      // Update local state
      setHouseholdMembers((prevMembers) =>
        prevMembers.map((member) => {
          if (member.id === memberId) {
            return {
              ...member,
              [section]: response.data,
            };
          }
          return member;
        }),
      );
    } catch (error) {
      logger.error("Error saving data:", error);
      // TODO: Show error message to user
    }
  };

  // Metric generators (same as before, but now member-specific)
  const getBasicMetrics = (memberData) => {
    if (!memberData?.basicAssessment) return [];
    const {
      baseAge,
      familyHistoryImpact,
      lifestyleImpact,
      healthStatusImpact,
      calculatedLifeExpectancy,
    } = memberData.basicAssessment;
    return [
      { label: "Base Age", value: baseAge },
      {
        label: "Family Impact",
        value:
          familyHistoryImpact > 0
            ? `+${familyHistoryImpact}`
            : familyHistoryImpact,
      },
      {
        label: "Lifestyle Impact",
        value: lifestyleImpact > 0 ? `+${lifestyleImpact}` : lifestyleImpact,
      },
      {
        label: "Health Impact",
        value:
          healthStatusImpact > 0
            ? `+${healthStatusImpact}`
            : healthStatusImpact,
      },
      { label: "Life Expectancy", value: calculatedLifeExpectancy },
    ];
  };

  const getRecommendedMetrics = (memberData) => {
    if (!memberData?.recommendedAssessment) return [];
    const { familyHistory, selfAssessment } = memberData.recommendedAssessment;
    return [
      {
        label: "Father's Age",
        value: familyHistory?.fatherAgeAtDeath || "N/A",
      },
      {
        label: "Mother's Age",
        value: familyHistory?.motherAgeAtDeath || "N/A",
      },
      { label: "Siblings", value: familyHistory?.siblings?.length || 0 },
      { label: "Health Score", value: `${selfAssessment?.healthScore}/100` },
      {
        label: "Conditions",
        value: selfAssessment?.majorHealthConditions?.length || 0,
      },
    ];
  };

  const getAdvancedMetrics = (memberData) => {
    if (!memberData?.advancedAssessment) return [];
    const { physicalHealth, lifestyle, mentalHealth } =
      memberData.advancedAssessment;
    return [
      { label: "BMI", value: physicalHealth?.bmi || "N/A" },
      {
        label: "Exercise",
        value:
          EXERCISE_FREQUENCY.find(
            (f) => f.value === lifestyle?.exerciseFrequency,
          )?.label || "N/A",
      },
      {
        label: "Stress Level",
        value:
          STRESS_LEVELS.find((s) => s.value === mentalHealth?.stressLevel)
            ?.label || "N/A",
      },
    ];
  };

  const getInsuranceMetrics = (memberData) => {
    if (!memberData?.insuranceInfo) return [];
    return [
      {
        label: "Life Policies",
        value: memberData.insuranceInfo.lifeInsurance?.length || 0,
      },
      {
        label: "Additional Coverage",
        value: memberData.insuranceInfo.additionalCoverage?.length || 0,
      },
    ];
  };

  const getEstatePlanMetrics = (memberData) => {
    if (!memberData?.estatePlan) return [];
    return [
      {
        label: "Documents",
        value: memberData.estatePlan.estatePlanDetails?.length || 0,
      },
      {
        label: "Status",
        value: memberData.estatePlan.hasEstatePlan
          ? "Active"
          : memberData.estatePlan.futureInterest?.status === "interested"
            ? "Interested"
            : "Not Started",
      },
    ];
  };

  const getHealthAssessmentStatus = (memberData) => {
    // Check if all assessments are completed
    const basicCompleted = memberData.basicAssessment?.assessment_date;
    const recommendedCompleted =
      memberData.recommendedAssessment?.assessment_date;
    const advancedCompleted = memberData.advancedAssessment?.assessment_date;

    if (basicCompleted && recommendedCompleted && advancedCompleted) {
      return <Chip size="small" label="All Completed" color="success" />;
    }

    // Show individual assessment status
    const statuses = [];
    if (advancedCompleted) {
      statuses.push("Advanced (Completed)");
    }
    if (recommendedCompleted) {
      statuses.push("Recommended (Completed)");
    }
    if (basicCompleted) {
      statuses.push("Basic (Completed)");
    }

    if (statuses.length > 0) {
      return <Chip size="small" label={statuses.join(", ")} color="success" />;
    }

    return <Chip size="small" label="Basic (Not Started)" color="default" />;
  };

  const renderMemberAssessments = (memberId, memberData) => (
    <Box sx={{ "& .MuiAccordion-root": { mb: 2 } }}>
      <Accordion
        expanded={expanded.includes("health")}
        onChange={handleAccordionChange("health")}
        defaultExpanded
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Typography fontWeight="bold">Health Assessments</Typography>
            {!expanded.includes("health") &&
              getHealthAssessmentStatus(memberData)}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <UnifiedAssessmentCard
                member={memberData}
                onStartAssessment={(step) =>
                  handleModalToggle(memberId, step, true)
                }
                basicAssessment={memberData.basicAssessment}
                recommendedAssessment={memberData.recommendedAssessment}
                advancedAssessment={memberData.advancedAssessment}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel),
    );
  };

  return (
    <Box className="p-4">
      {isLoading ? (
        <Box className="flex justify-center items-center min-h-[200px]">
          <CircularProgress className="text-blue-600" />
        </Box>
      ) : error ? (
        <Box className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
          <Typography>{error}</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {householdMembers.map((member) => (
            <Grid item xs={12} key={member.id}>
              <Box className={`
                p-4 rounded-lg border
                ${isDarkMode 
                  ? 'bg-neutral-800 border-neutral-700' 
                  : 'bg-white border-gray-200'
                }
              `}>
                <Box className="flex items-center gap-4 mb-4">
                  <Avatar
                    src={member.avatar_url}
                    alt={formatName(member.name)}
                    className="w-16 h-16"
                  />
                  <Box>
                    <Typography className="text-xl font-semibold">
                      {formatName(member.name)}
                    </Typography>
                    <Typography className="text-sm text-gray-600 dark:text-gray-300">
                      {member.role || "Member"}
                    </Typography>
                  </Box>
                </Box>

                <Divider className="my-4" />

                {/* Health Assessment Section */}
                <Accordion
                  expanded={expanded.includes("health")}
                  onChange={handleAccordionChange("health")}
                  className={`
                    mb-4 shadow-sm
                    ${isDarkMode 
                      ? 'bg-neutral-800 border border-neutral-700' 
                      : 'bg-white border border-gray-200'
                    }
                  `}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="hover:bg-gray-50 dark:hover:bg-neutral-700/50"
                  >
                    <Typography className="font-medium">
                      Health Assessment
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderMemberAssessments(member.id, member)}
                  </AccordionDetails>
                </Accordion>

                {/* Insurance Section */}
                <Accordion
                  expanded={expanded.includes("insurance")}
                  onChange={handleAccordionChange("insurance")}
                  className={`
                    mb-4 shadow-sm
                    ${isDarkMode 
                      ? 'bg-neutral-800 border border-neutral-700' 
                      : 'bg-white border border-gray-200'
                    }
                  `}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="hover:bg-gray-50 dark:hover:bg-neutral-700/50"
                  >
                    <Typography className="font-medium">
                      Insurance Coverage
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <InsuranceSection
                      memberId={member.id}
                      insuranceData={member.insurance}
                      onUpdate={(data) =>
                        handleUpdate(member.id, "insurance", data)
                      }
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Estate Planning Section */}
                <Accordion
                  expanded={expanded.includes("estate")}
                  onChange={handleAccordionChange("estate")}
                  className={`
                    mb-4 shadow-sm
                    ${isDarkMode 
                      ? 'bg-neutral-800 border border-neutral-700' 
                      : 'bg-white border border-gray-200'
                    }
                  `}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="hover:bg-gray-50 dark:hover:bg-neutral-700/50"
                  >
                    <Typography className="font-medium">
                      Estate Planning
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <EstatePlanSection
                      memberId={member.id}
                      estatePlanData={member.estatePlan}
                      onUpdate={(data) =>
                        handleUpdate(member.id, "estatePlan", data)
                      }
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Assessment Wizard Modal */}
      <UnifiedAssessmentWizard
        open={modalOpen.type === "assessment"}
        memberId={modalOpen.memberId}
        onClose={() => handleModalToggle(null, null, false)}
        onSave={(data) => handleUpdate(modalOpen.memberId, "assessment", data)}
      />
    </Box>
  );
};

export default HealthLife;
